import { useFormikContext } from 'formik';
import React, { FC, useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import { toast } from 'react-toastify';
import ActivityFrame from '../../components/activity-frame/activity-frame';
import { EVENT_ACTION_KEYS } from '../../constants/command-status';
import { DEF_PG_SIZE_SUB_MIN } from '../../constants/maximum-page-size';
import { useLocale } from "../../contexts/localeContext";
import { TableContextProvider } from '../../contexts/table/TableContextProvider';
import { TranslationKeys } from "../../contexts/TranslationKeys";
import { Button } from '../../components/button/Button';
import deviceEventColumnDescriptor from '../../models/ColumnDefinitions/DeviceEventActionColumns';
import { ListData } from '../../models/ListData';
import { ListQuery } from '../../models/ListQuery';
import { MultiSelectOption } from '../../models/MultiSelectOption';
import deviceEventSearchDescriptor from '../../models/search/DeviceEventActionSearch';
import { getAllDeviceEventActions, getDeviceEventActionIds, getDeviceEventActions, getDeviceEventActionTypeNamesWithPagination, storeEventAction } from "../../services/deviceEventActionService";
import { getAllAlloweOrDeniedActionsId } from '../../services/executeCommandService';
import { getErrorMessage, showError } from '../../services/utilities/ServiceError';
import { IAppState } from '../../store/types';
import { appendFilter } from '../../utilities/filters';
import AssignWizard from '../assignWizard/AssignWizard';
import Label from '../label/label';
import PaginatedSearchableSelectField from '../selection-field/paginated-searchable-select-field';
import TableTab from "../tabs/table-tab";

interface Props {
    showProvidedSelectOptions: boolean,
    itemId: string;
}

const DeviceEventActions: FC<Props> = ({ itemId, showProvidedSelectOptions }) => {
    const { translate } = useLocale();
    const { values  } = useFormikContext<any>();
    const siteId = useSelector((state: IAppState) => state.site.site.value)
    const [showAddModal, setShowAddModal] = useState(false);
    const [selectedEventOption, setSelectedEventOption] = useState('89');
    const [selectedEventOptionLabel, setSelectedEventOptionLabel] = useState<string>("");
    const title = translate.byKey(TranslationKeys.assignedActions);
    const [selectedIds, setSelectedIds] = useState<string[]>([]);
    const [oldSelectedIds, setOldSelectedIds] = useState<string[]>([]);
    const [busy, setBusy] = useState(false);
    const [isInitialised, setIsInitialised] = useState(false);

    useEffect(() => {
        dataRefresh((listParameter?: ListQuery) => getNewDeviceEventActions(listParameter))
        buildDeviceActionEventTypes();
    }, [itemId])

    const buildDeviceActionEventTypes = async () => {
        try {
            const eventActionsTypes = await getDeviceEventActionTypeNamesWithPagination(showProvidedSelectOptions, { filters: appendFilter(itemId, 'Device Improx Id', 'deviceImprox.id', []) });
            if (eventActionsTypes.rows.length > 0) {
                const item: MultiSelectOption = {
                    label: eventActionsTypes.rows[0].label,
                    value: eventActionsTypes.rows[0].value
                }
                const fakeOnclickEvent = {} as React.MouseEvent<HTMLButtonElement, MouseEvent>
                onEventTypeSelect(fakeOnclickEvent, item);
                setIsInitialised(true);
            }
        } catch (error) {
            showError(error, toast.warn);
        }
    }

    useEffect(() => {
        buildAllowedorDeniedActions();
        dataRefresh((listParameter?: ListQuery) => getNewDeviceEventActions(listParameter))
    }, [selectedEventOption, showAddModal])

    const buildAllowedorDeniedActions = async () => {
        try {
            const allActionsId = selectedEventOption === EVENT_ACTION_KEYS.ALL_ALLOWED_KEY || selectedEventOption === EVENT_ACTION_KEYS.ALL_DENIED_KEY
                ? await getAllAlloweOrDeniedActionsId(itemId, selectedEventOption)
                : [];
            const eventActions = selectedEventOption === EVENT_ACTION_KEYS.ALL_ALLOWED_KEY || selectedEventOption === EVENT_ACTION_KEYS.ALL_DENIED_KEY
                ? await getDeviceEventActionIds(allActionsId.data ? allActionsId.data.actions : undefined)
                : await getDeviceEventActions(itemId, selectedEventOption);
            const eventActionIds = eventActions?.rows.map(eventAction => eventAction.id.toString())
            setSelectedIds(eventActionIds)
            setOldSelectedIds(eventActionIds)
        } catch (error) {
            toast.warn(getErrorMessage(error));
        }
    }

    const assignEventActions = async () => {
        const newSelectedIds = selectedIds.filter(value => value !== "")
        setOldSelectedIds(newSelectedIds)

        const toBeRemoved = oldSelectedIds.filter(value => {
            if (!!!newSelectedIds.includes(value)) {
                return value
            }
        });

        const toBeAdded = newSelectedIds.filter(value => {
            if (!!!oldSelectedIds.includes(value)) {
                return value
            }
        });

        toBeRemoved.filter(id => id !== "").map(async event => {
            setBusy(true);
            storeEventAction({
                eventDeviceImproxId: itemId,
                eventActionId: event.toString(),
                deviceEventTypeId: selectedEventOption.toString(),
                requestType: EVENT_ACTION_KEYS.UNASSIGN_KEY
            })
                .then(() => toast.success(translate.byKey(TranslationKeys.eventActionsUpdated)))
                .catch((error) => showError(error, toast.warn))
                .finally(() => setBusy(false));
        })

        toBeAdded.length > 0 && toBeAdded.map(async id => {
            setBusy(true);
            storeEventAction({
                eventDeviceImproxId: itemId,
                eventActionId: id.toString(),
                deviceEventTypeId: selectedEventOption.toString(),
                requestType: EVENT_ACTION_KEYS.ASSIGN_KEY
            })
                .then(() => toast.success(translate.byKey(TranslationKeys.eventActionsUpdated)))
                .catch((error) => showError(error, toast.warn))
                .finally(() => setBusy(false));
        })
    }

    const getNewDeviceEventActions = async (listParameters) => {
        try {
            const allActionsId = selectedEventOption === EVENT_ACTION_KEYS.ALL_ALLOWED_KEY || selectedEventOption === EVENT_ACTION_KEYS.ALL_DENIED_KEY
                ? await getAllAlloweOrDeniedActionsId(itemId, selectedEventOption) : [];
            const eventActions = selectedEventOption === EVENT_ACTION_KEYS.ALL_ALLOWED_KEY || selectedEventOption === EVENT_ACTION_KEYS.ALL_DENIED_KEY
                ? await getDeviceEventActionIds(allActionsId.data ? allActionsId.data.actions : undefined, listParameters)
                : await getDeviceEventActions(itemId, selectedEventOption, listParameters)
            return eventActions;
        } catch (error) {
            return new Promise<ListData>((resolve, reject) =>
                resolve({
                    rows: [],
                    pagination: { maxPageSize: 1, pageNumber: 1, totalCount: 0, totalPages: 0 }
                })
            );
        }
    }

    const filterToNameOnly = searchDescriptor => {
        return searchDescriptor.filter(item => item.accessor === 'name');
    };

    const onEventTypeSelect = (event, item?: MultiSelectOption) => {
        setSelectedEventOption(item?.value ?? "");
        setSelectedEventOptionLabel(item?.label ?? "");
    }

    // default to a method that returns nothing
    // when putting a method in a state variable, useState requires us to return the method from a method, hence the wrapped inner method.
    const [dataRefresh, setDataRefresh] = useState(() => (dataMethod?: (query?: ListQuery) => Promise<ListData>) => { return });

    const manualDataRefresh = (refreshData: (dataMethod?: (query?: ListQuery) => Promise<ListData>) => void) => {
        setDataRefresh(() => refreshData);
    }
    // <-- end context

    return (
        <>
            <ActivityFrame busy={busy} className='flex flex-col flex-grow bg-white'>
                <TableContextProvider
                    dataMethod={getNewDeviceEventActions}
                    tableDescriptor={deviceEventColumnDescriptor}
                    searchDescriptor={deviceEventSearchDescriptor}
                    useSearch
                    useSort
                    useFilter
                    useLegend
                    useTable
                    usePagination
                    manualDataRefresh={manualDataRefresh}
                    maxPageSize={DEF_PG_SIZE_SUB_MIN}
                    onAddNew={() => setShowAddModal(true)}>
                    <TableTab
                        title={title}
                        selectRows={false}
                        cyName='deviceEventActions'>
                        <div className="w-auto">
                            <div className="flex flex-row justify-between ">
                                <div className="flex flex-col pr-6">
                                    <Label htmlFor='eventOptions'
                                        className="text-xs text-gray-600 mt-1">{translate.byKey(TranslationKeys.eventTypes)}:</Label>
                                </div>
                                <div className="flex flex-col flex-1 w-64">
                                    <div className="flex flex-col">
                                        {isInitialised &&
                                        <PaginatedSearchableSelectField
                                            name={translate.byKey(TranslationKeys.eventTypes)}
                                            key="deviceEventTypeId"
                                            accessor='deviceEventTypeId'
                                            dataMethod={(listQuery) => getDeviceEventActionTypeNamesWithPagination(showProvidedSelectOptions, { ...listQuery, filters: appendFilter(itemId, 'Device Improx Id', 'deviceImprox.id', listQuery?.filters ?? []) })}
                                            searchAccessor={'eventType.nameOriginal'}
                                            onSelect={(event, item) => onEventTypeSelect(event, item)}
                                            customSelectedOption={selectedEventOption}
                                            selectedItemLabel={selectedEventOptionLabel}/>}
                                    </div>
                                </div>
                                <Button variant="submit" shape="rounded" className="ml-8 py-1 px-5" onClick={() => setShowAddModal(true)}>
                                    {translate.byKey(TranslationKeys.manage)}
                                </Button>
                            </div>
                        </div>
                    </TableTab>
                </TableContextProvider>
                <AssignWizard
                    isOpen={showAddModal}
                    dataMethod={(listParameters?: ListQuery) => getAllDeviceEventActions({ ...listParameters, filters: appendFilter(values.siteId ?? siteId, 'Site id', 'deviceImprox.site.id', listParameters?.filters ?? []) })}
                    lookup={[]}
                    selectedIds={selectedIds}
                    setSelectedIds={setSelectedIds}
                    onClose={() => { setShowAddModal(false) }}
                    title={translate.byKey(TranslationKeys.assignDeviceActions) + " " + selectedEventOptionLabel}
                    columnDescriptors={
                        {
                            columns: deviceEventColumnDescriptor,
                            searchColumns: filterToNameOnly(deviceEventSearchDescriptor),
                        }
                    }
                    handleOnSubmit={assignEventActions}
                    tableTitle={translate.byKey(TranslationKeys.assignActions)} />
            </ActivityFrame>
        </>
    );
};

export default DeviceEventActions;
